import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import * as React from 'react'

import { toast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/useDebounce'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import groupService from '@/services/group.service'
import { ErrorResponse } from '@/utils/axios-error-handle'
import { convertToSlug } from '@/utils/string-to-slug'

import { Textarea } from '../ui/textarea'

import { Channel, ChannelStatus, GroupChannel } from '@/types/group-channel.type'

interface EditChannelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  channel: Channel | null
  groupId: string | undefined
}

interface DeleteChannelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  channel: Channel | null
}

export function EditChannelDialog({ open, onOpenChange, channel, groupId }: EditChannelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa kênh</DialogTitle>
          <DialogDescription>Cập nhật thông tin kênh. Nhấn lưu khi hoàn tất.</DialogDescription>
        </DialogHeader>
        <EditChannelForm onOpenChange={onOpenChange} channel={channel} groupId={groupId} />
      </DialogContent>
    </Dialog>
  )
}

function EditChannelForm({
  onOpenChange,
  channel,
  groupId,
}: {
  onOpenChange: (open: boolean) => void
  channel: Channel | null
  groupId: string | undefined
}) {
  const queryClient = useQueryClient()
  const [displayName, setDisplayName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [status, setStatus] = React.useState<ChannelStatus>('ACTIVE')

  const name = useDebounce(convertToSlug(displayName), 200)

  React.useEffect(() => {
    if (channel) {
      setDisplayName(channel.name)
      setDescription(channel.description)
      setStatus(channel.status)
    }
  }, [channel])

  const updateChannelMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; status: ChannelStatus }) => {
      if (!channel) throw new Error('Channel not found')
      return groupService.updateChannel(channel.id, {
        name: data.name,
        description: data.description,
        status: data.status,
        groupId: groupId,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum-groups'] })
      queryClient.invalidateQueries({ queryKey: ['forum-channels'] })
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
      onOpenChange(false)
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Error updating channel:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to update channel',
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!channel || !name.trim()) return
    updateChannelMutation.mutate({ name: name.trim(), description, status })
  }
  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-4 py-4">
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="name" className="text-right">
            Tên kênh
          </Label>
          <div className="col-span-3">
            <Input
              id="name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              className="mb-2"
              placeholder="Nhập tên kênh..."
              required
            />
            {name && (
              <div className="text-xs text-muted-foreground">
                Slug: <code className="bg-muted px-1 py-0.5 rounded">{name}</code>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="description" className="text-right">
            Description
          </Label>
          <div className="col-span-3">
            <Textarea
              id="description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="mb-2"
              placeholder="Enter description..."
              rows={3}
            />
          </div>
        </div>
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="status" className="text-right">
            Trạng thái
          </Label>
          <Select value={status} onValueChange={(value: ChannelStatus) => setStatus(value)}>
            <SelectTrigger className="col-span-3">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE" className="hover:bg-accent hover:text-accent-foreground">
                Hoạt động
              </SelectItem>
              <SelectItem value="INACTIVE" className="hover:bg-accent hover:text-accent-foreground">
                Không hoạt động
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Hủy
        </Button>
        <Button type="submit" disabled={updateChannelMutation.isPending || !name.trim()}>
          {updateChannelMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Lưu thay đổi
        </Button>
      </DialogFooter>
    </form>
  )
}

interface CreateChannelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: GroupChannel | null
}

export function CreateChannelDialog({ open, onOpenChange, group }: CreateChannelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Tạo kênh mới</DialogTitle>
          <DialogDescription>Tạo một kênh mới trong nhóm "{group?.name}". Nhấn tạo khi hoàn tất.</DialogDescription>
        </DialogHeader>
        <CreateChannelForm onOpenChange={onOpenChange} group={group} />
      </DialogContent>
    </Dialog>
  )
}

export function CreateChannelForm({
  onOpenChange,
  group,
}: {
  onOpenChange: (open: boolean) => void
  group: GroupChannel | null
}) {
  const queryClient = useQueryClient()
  const [displayName, setDisplayName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [status, setStatus] = React.useState<ChannelStatus>('ACTIVE')

  const name = useDebounce(convertToSlug(displayName), 100)

  const createChannelMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; status: ChannelStatus; groupId: string | undefined }) =>
      groupService.createChannel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum-groups'] })
      queryClient.invalidateQueries({ queryKey: ['forum-channels'] })
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
      setDisplayName('')
      setDescription('')
      setStatus('ACTIVE')
      onOpenChange(false)
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Error creating channel:', error)
      const _error = error as unknown as ErrorResponse
      toast({
        title: 'Error',
        description: _error.response?.data.message || 'Failed to create channel',
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    createChannelMutation.mutate({ name: name.trim(), status, description, groupId: group ? group.id : undefined })
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-4 py-4">
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="create-name" className="text-right">
            Tên kênh
          </Label>
          <div className="col-span-3">
            <Input
              id="create-name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              className="mb-2"
              placeholder="Nhập tên kênh..."
              required
            />
            {name && (
              <div className="text-xs text-muted-foreground">
                Slug: <code className="bg-muted px-1 py-0.5 rounded">{name}</code>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="create-description" className="text-right">
            Description
          </Label>
          <div className="col-span-3">
            <Textarea
              id="create-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="mb-2"
              placeholder="Enter description..."
              rows={3}
            />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Hủy
        </Button>
        <Button type="submit" disabled={createChannelMutation.isPending || !name.trim()}>
          {createChannelMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Tạo kênh
        </Button>
      </DialogFooter>
    </form>
  )
}

export function DeleteChannelDialog({ open, onOpenChange, channel }: DeleteChannelDialogProps) {
  const queryClient = useQueryClient()

  const deleteChannelMutation = useMutation({
    mutationFn: () => {
      if (!channel) throw new Error('Channel not found')
      return groupService.deleteChannel(channel.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum-groups'] })
      queryClient.invalidateQueries({ queryKey: ['forum-channels'] })
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
      onOpenChange(false)
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Error deleting channel:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete channel',
        variant: 'destructive',
      })
    },
  })

  const handleDelete = () => {
    if (!channel) return
    deleteChannelMutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Xóa kênh</DialogTitle>
          <DialogDescription>
            Bạn có chắc chắn muốn xóa kênh "#{channel?.name}"? Hành động này không thể hoàn tác và sẽ xóa tất cả các bài
            viết trong kênh.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Hủy
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteChannelMutation.isPending}>
            {deleteChannelMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Xóa kênh
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
